import React, {FormEvent, useCallback, useEffect, useMemo, useRef} from "react";
import {connect} from "react-redux";
import {CheckboxItem, Text, styled, theme} from "@reside/ui";
import {UserDto, UserPermissionEnum} from "../../../../utils/types/user";
import {select} from "../../../../store";
import {QuicksightDashboard} from "../../../../services/QuicksightService";
import {useQuicksightAdminDashboards} from "../../hooks/useQuicksightAdminDashboards";
import {useQuicksightDashboardsPermissionMutation} from "../../hooks/useQuicksightDashboardsPermissionMutation";
import {useQuicksightUserDashboards} from "../../hooks/useQuicksightUserDashboards";
import {ClearQuicksightCacheButton} from "../clear-quicksight-cache-button";
import {
  PermissionCategoryCard,
  PermissionCategoryItem,
} from "../permissions-macro-checkbox/PermissionCategoryCard";
import {syncQuicksightDashboardsReportingPermission} from "./syncQuicksightDashboardsReportingPermission";
import {
  QuicksightDashboardDeferredSaveHandlers,
  useQuicksightDashboardDraft,
} from "./useQuicksightDashboardDraft";

const EMPTY_DASHBOARDS: ReadonlyArray<QuicksightDashboard> = [];

type ReportingPermissionSyncProps = Readonly<{
  activePermissions?: ReadonlyArray<UserPermissionEnum>;
  deferSave?: boolean;
  onDeferredSaveHandlersChange?: (
    handlers: QuicksightDashboardDeferredSaveHandlers | null,
  ) => void;
  onSetPermission?: (permissions: ReadonlyArray<UserPermissionEnum>) => void;
}>;

const RoleRenderer = ({
  user,
  userId,
  isResideAdmin,
  activePermissions,
  deferSave,
  onDeferredSaveHandlersChange,
  onSetPermission,
}: {
  isResideAdmin: boolean;
  userId: string;
  user: UserDto;
} & ReportingPermissionSyncProps) =>
  isResideAdmin ? (
    <ResideAdminRenderer
      userId={userId}
      activePermissions={activePermissions}
      deferSave={deferSave}
      onDeferredSaveHandlersChange={onDeferredSaveHandlersChange}
      onSetPermission={onSetPermission}
    />
  ) : (
    <BasicUserRenderer
      userId={userId}
      user={user}
      activePermissions={activePermissions}
      deferSave={deferSave}
      onDeferredSaveHandlersChange={onDeferredSaveHandlersChange}
      onSetPermission={onSetPermission}
    />
  );

/**
 * When the user managing permissions is not reside admin, his own permission are those he can grant to other users.
 */
const BasicUserRenderer = ({
  user,
  userId,
  activePermissions,
  deferSave,
  onDeferredSaveHandlersChange,
  onSetPermission,
}: {userId: string; user: UserDto} & ReportingPermissionSyncProps) => {
  const {
    data: userDashboardsData,
    isLoading,
    isFetching,
    refetch,
  } = useQuicksightUserDashboards(user.id);
  const userDashboards = userDashboardsData ?? EMPTY_DASHBOARDS;

  return (
    <Renderer
      userId={userId}
      dashboardOptions={userDashboards}
      isLoadingDashboardOptions={isLoading || isFetching}
      refetchDashboardOptions={refetch}
      activePermissions={activePermissions}
      deferSave={deferSave}
      onDeferredSaveHandlersChange={onDeferredSaveHandlersChange}
      onSetPermission={onSetPermission}
    />
  );
};

/**
 * Reside admin users don't have permission associated with the user account, but fetch all available dashboards instead.
 */
const ResideAdminRenderer = ({
  userId,
  activePermissions,
  deferSave,
  onDeferredSaveHandlersChange,
  onSetPermission,
}: {userId: string} & ReportingPermissionSyncProps) => {
  const {
    data: optionsData,
    isLoading,
    isFetching,
    refetch,
  } = useQuicksightAdminDashboards();
  const options = optionsData ?? EMPTY_DASHBOARDS;

  return (
    <Renderer
      userId={userId}
      dashboardOptions={options}
      isLoadingDashboardOptions={isLoading || isFetching}
      refetchDashboardOptions={refetch}
      headerActions={<ClearQuicksightCacheButton onSuccess={refetch} />}
      activePermissions={activePermissions}
      deferSave={deferSave}
      onDeferredSaveHandlersChange={onDeferredSaveHandlersChange}
      onSetPermission={onSetPermission}
    />
  );
};

const Renderer = ({
  userId,
  dashboardOptions,
  isLoadingDashboardOptions,
  refetchDashboardOptions,
  headerActions,
  activePermissions,
  deferSave = false,
  onDeferredSaveHandlersChange,
  onSetPermission,
}: {
  userId: string;
  dashboardOptions: ReadonlyArray<Partial<QuicksightDashboard>>;
  isLoadingDashboardOptions: boolean;
  refetchDashboardOptions: () => void;
  headerActions?: React.ReactNode;
} & ReportingPermissionSyncProps) => {
  const {
    data: userDashboardsData,
    isLoading: isLoadingUserDashboards,
    isFetching: isFetchingUserDashboards,
    refetch,
  } = useQuicksightUserDashboards(userId);
  const userDashboards = userDashboardsData ?? EMPTY_DASHBOARDS;

  const {mutate, isLoading: isSavingPermissions} =
    useQuicksightDashboardsPermissionMutation({
      onConflict: () => {
        refetchDashboardOptions();
      },
    });

  const loading =
    isLoadingDashboardOptions ||
    isLoadingUserDashboards ||
    isFetchingUserDashboards;

  const dashboardIds = dashboardOptions
    .map(({id}) => id)
    .filter((id): id is string => Boolean(id));

  const savedCheckedValues = useMemo(
    () => userDashboards.map(dashboard => dashboard.id),
    [userDashboards],
  );

  const onPersistSuccess = useCallback(() => {
    refetch();
  }, [refetch]);

  const persistDashboardChange = useCallback(
    ({
      userId: targetUserId,
      dashboard,
    }: {
      userId: string;
      dashboard: {dashboardId: string; checked: boolean};
    }) =>
      new Promise<void>((resolve, reject) => {
        mutate(
          {
            userId: targetUserId,
            dashboard,
            onSuccess: () => {
              resolve(undefined);
            },
          },
          {
            onError: reject,
          },
        );
      }),
    [mutate],
  );

  const dashboardDraft = useQuicksightDashboardDraft({
    userId,
    savedCheckedIds: savedCheckedValues,
    persistDashboardChange,
    onPersistSuccess,
  });

  const deferredSaveHandlersRef = useRef<QuicksightDashboardDeferredSaveHandlers | null>(
    null,
  );

  if (deferSave) {
    deferredSaveHandlersRef.current = {
      discard: dashboardDraft.discard,
      pendingChangeCount: dashboardDraft.pendingChangeCount,
      save: dashboardDraft.save,
    };
  } else {
    deferredSaveHandlersRef.current = null;
  }

  useEffect(() => {
    if (!deferSave) {
      onDeferredSaveHandlersChange?.(null);
      return;
    }

    onDeferredSaveHandlersChange?.({
      discard: () => {
        deferredSaveHandlersRef.current?.discard();
      },
      pendingChangeCount: dashboardDraft.pendingChangeCount,
      save: async () => {
        await deferredSaveHandlersRef.current?.save();
      },
    });
  }, [
    deferSave,
    dashboardDraft.pendingChangeCount,
    onDeferredSaveHandlersChange,
  ]);

  const checkedValues = deferSave
    ? dashboardDraft.checkedValues
    : savedCheckedValues;
  const everyChecked =
    dashboardIds.length > 0 &&
    dashboardIds.every(dashboardId => checkedValues.includes(dashboardId));
  const someChecked = dashboardIds.some(dashboardId =>
    checkedValues.includes(dashboardId),
  );
  const selectedCount = dashboardIds.filter(dashboardId =>
    checkedValues.includes(dashboardId),
  ).length;

  const syncReportingPermission = (hasDashboardAccess: boolean) => {
    if (!onSetPermission || !activePermissions) {
      return;
    }

    const nextPermissions = syncQuicksightDashboardsReportingPermission(
      hasDashboardAccess,
      activePermissions,
    );

    if (nextPermissions !== activePermissions) {
      onSetPermission(nextPermissions);
    }
  };

  const willHaveDashboardAccess = (
    nextCheckedValues: ReadonlyArray<string>,
  ) => nextCheckedValues.length > 0;

  const applyDashboardChecked = (
    dashboardId: string,
    checked: boolean,
    options?: Readonly<{
      skipReportingSync?: boolean;
      onSuccess?: () => void;
    }>,
  ) => {
    if (deferSave) {
      dashboardDraft.setDashboardChecked(dashboardId, checked);

      if (!options?.skipReportingSync) {
        const nextCheckedValues = checked
          ? [...checkedValues.filter(id => id !== dashboardId), dashboardId]
          : checkedValues.filter(id => id !== dashboardId);

        syncReportingPermission(willHaveDashboardAccess(nextCheckedValues));
      }

      options?.onSuccess?.();

      return;
    }

    const nextCheckedValues = checked
      ? [...checkedValues.filter(id => id !== dashboardId), dashboardId]
      : checkedValues.filter(id => id !== dashboardId);

    mutate({
      userId,
      dashboard: {dashboardId, checked},
      onSuccess: () => {
        if (!options?.skipReportingSync) {
          syncReportingPermission(willHaveDashboardAccess(nextCheckedValues));
        }

        (options?.onSuccess ?? refetch)();
      },
    });
  };

  const onGroupChange = (event: FormEvent<HTMLInputElement>) => {
    const checked = event.currentTarget.checked;
    const dashboardsToUpdate = dashboardIds.filter(
      dashboardId => checked !== checkedValues.includes(dashboardId),
    );

    if (dashboardsToUpdate.length === 0) {
      return;
    }

    if (deferSave) {
      dashboardDraft.setGroupChecked(dashboardIds, checked);
      syncReportingPermission(checked);
      return;
    }

    // Sync reporting permission once from the intended final state. Per-dashboard
    // success handlers must not re-derive this during bulk updates — they all share
    // the same stale checkedValues snapshot and would flip QUICKSIGHT_DASHBOARDS
    // back on while revokes are still in flight.
    syncReportingPermission(checked);

    let completed = 0;

    dashboardsToUpdate.forEach(dashboardId => {
      applyDashboardChecked(dashboardId, checked, {
        skipReportingSync: true,
        onSuccess: () => {
          completed += 1;

          if (completed === dashboardsToUpdate.length) {
            refetch();
          }
        },
      });
    });
  };

  const label =
    !deferSave && isSavingPermissions
    ? "Quicksight Dashboards (Saving...)"
    : "Quicksight Dashboards";

  return (
    <PermissionCategoryCard
      label={label}
      selectedCount={selectedCount}
      totalCount={dashboardIds.length}
      someChecked={someChecked}
      everyChecked={everyChecked}
      disabled={loading || (!deferSave && isSavingPermissions)}
      headerActions={headerActions}
      onGroupChange={onGroupChange}
    >
      {loading ? (
        <LoadingState>Loading...</LoadingState>
      ) : (
        dashboardOptions.map(({id: dashboardId, name}) => (
          <PermissionCategoryItem key={dashboardId}>
            <CheckboxItem
              value={dashboardId}
              label={name}
              checked={checkedValues.includes(dashboardId)}
              disabled={loading || (!deferSave && isSavingPermissions)}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                applyDashboardChecked(
                  dashboardId,
                  event.currentTarget.checked,
                )
              }
            />
          </PermissionCategoryItem>
        ))
      )}
    </PermissionCategoryCard>
  );
};

const LoadingState = styled(Text)`
  color: ${theme.color.gray150};
  display: block;
  font-size: 14px;
  line-height: 20px;
  padding: 16px;
`;

const mapStateToProps = (state: any) => ({
  user: select.adminSession.user(state),
  isResideAdmin: select.adminSession.isResideAdmin(state),
});

export const QuicksightDashboardsMacroCheckbox =
  connect(mapStateToProps)(RoleRenderer);
